import { useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
    hasPermission,
    canEditOwnIdea,
    ROLE_LABELS,
    ROLE_BADGE_COLOR,
    DISABLED_BTN_CLS,
    permissionTitle as titleFn,
    ROLE_PERMISSIONS,
    landingPathForRole,
} from "../lib/permissions";

/**
 * Centralized permission hook (Build Prompt #14).
 * Returns booleans for every permission the UI cares about, plus role-family flags
 * (isSystems, isField, isSupply, isDemand, isLogistics, isOversight, isAdminFamily).
 *
 * Hard-masking rule: if a permission is false, the corresponding UI surface MUST be
 * removed from the DOM (return null at the call site, not just a disabled button).
 */
export function usePermissions() {
    const { user } = useAuth();

    return useMemo(() => {
        const role = user?.role ?? null;
        const isAuthed = !!user && user !== false;
        const can = (perm) => hasPermission(user, perm);
        const permissionTitle = (perm) => titleFn(user, perm);

        const isSuperAdmin = role === "super_admin";
        const isChiefOfOps = role === "chief_of_operations";
        const isOversight = role === "strategic_oversight";
        const isSystems = role === "systems_lead" || role === "systems_operator";
        const isField = role === "field_lead" || role === "field_operator";
        const isSupply = role === "supply_lead" || role === "supply_operator";
        const isDemand = role === "demand_lead" || role === "demand_operator";
        const isLogistics = role === "logistics_command" || role === "logistics_operator";
        const isMissionPartner = role === "mission_partner";

        // "Admin family" — anyone who can see the Admin Console (super_admin + chief).
        const isAdminFamily = isSuperAdmin || isChiefOfOps;

        return {
            // Identity
            user: user || null,
            role,
            isAuthed,
            isSuperAdmin,
            isChiefOfOps,
            isOversight,
            isSystems,
            isField,
            isSupply,
            isDemand,
            isLogistics,
            isMissionPartner,
            isAdminFamily,

            // Generic checkers
            can,
            permissionTitle,
            canEditIdea: (idea) => canEditOwnIdea(user, idea) || can("review_ideas"),

            // Visual
            roleLabel: ROLE_LABELS[role] || (role ?? "—").toString().toUpperCase(),
            roleBadgeClass: ROLE_BADGE_COLOR[role] || "border-hairline text-snow-dim",
            DISABLED_BTN_CLS,
            ROLE_PERMISSIONS,
            landingPath: landingPathForRole(role),

            // === Pre-computed permission booleans (alphabetical) ===
            // Admin Console
            manageUsers: can("manage_users"),
            manageInvitations: can("manage_invitations"),
            manageRolesEngine: can("manage_roles_engine"),
            viewSystemHealth: can("view_system_health"),

            // Master records
            manageTemplates: can("manage_templates"),
            manageControlModes: can("manage_control_modes"),

            // Products
            createProduct: can("create_product"),
            editProduct: can("edit_product"),
            deleteProduct: can("delete_product"),
            viewProducts: can("view_products"),
            viewProductsLimited: can("view_products_limited"),

            // Gates
            approveGate: can("approve_gate"),
            submitGateForReview: can("submit_gate_for_review"),
            uploadDeliverable: can("upload_deliverable"),
            requestApproval: can("request_approval"),
            killSwitch: can("kill_switch"),

            // Inventory / Orders / Vendors / Logistics
            manageInventory: can("manage_inventory"),
            viewInventory: can("view_inventory"),
            deleteInventory: can("delete_inventory"),
            manageOrders: can("manage_orders"),
            viewOrders: can("view_orders"),
            transitionOrder: can("transition_order"),
            approveOrderOverride: can("approve_order_override"),
            manageVendors: can("manage_vendors"),
            viewVendors: can("view_vendors"),
            viewLogistics: can("view_logistics"),

            // Field Intel
            fileFieldReport: can("file_field_report"),
            viewFieldIntel: can("view_field_intel"),
            resolveFieldReport: can("resolve_field_report"),
            deleteFieldReport: can("delete_field_report"),
            initiateRecert: can("initiate_recert"),

            // Innovation Funnel
            viewAllIdeas: can("view_all_ideas"),
            submitIdea: can("submit_idea"),
            editOwnIdea: can("edit_own_idea"),
            reviewIdeas: can("review_ideas"),
            deleteIdea: can("delete_idea"),
            appendConceptAssets: can("append_concept_assets"),

            // Dashboards
            viewConceptIntake: can("view_concept_intake"),
            viewDashboardOversight: can("view_dashboard_oversight"),

            // Approval Queue is a Systems Lead / Chief / Super Admin surface
            canSeeApprovalQueue: can("approve_gate"),
        };
    }, [user]);
}
